import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import DocumentError from "../../models/document-error";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import DocumentErrorDeleteModal from "./DocumentErrorDeleteModal";
import DocumentErrorUpdateModal from "./DocumentErrorUpdateModal";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { emptyValueOnUndefined } from "../../utils/fields";

const ITEMS_PER_PAGE = 10;

const DocumentErrorList = () => {
  const fields = [
    {
      key: "id",
      _classes: ["text-center"],
      label: "#",
    },
    {
      key: "errorCode",
      _classes: ["text-center"],
      label: "Código de error",
    },
    {
      key: "rawMessage",
      _classes: ["text-center"],
      label: "Mensaje",
    },
    {
      key: "description",
      _classes: ["text-center"],
      label: "Descripción",
    },
    {
      key: "possibleSolutions",
      _classes: ["text-center"],
      label: "Posibles Soluciones",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const [search, setSearch] = useState<string>("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documenterror, setDocumentError] = useState<DocumentError | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);
  const [documenterrors, setDocumentErrors] = useState<DocumentError[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    return map;
  };

  const fetchDocumentErrors = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    const documentErrorsStatus = await getList<DocumentError>(
      "/document_errors/",
      limit,
      offset,
      urlParams
    );
    if (documentErrorsStatus.status === SUCCESS) {
      if (documentErrorsStatus.data !== undefined) {
        const count = documentErrorsStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setDocumentErrors(documentErrorsStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = documentErrorsStatus.detail
        ? documentErrorsStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : ""
    );
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    setShowEditModal(true);
  };

  const onUpdateClick = (documenterror: DocumentError) => {
    setDocumentError(documenterror);
    setShowEditModal(true);
  };

  const onDeleteClick = (documenterror: DocumentError) => {
    setDocumentError(documenterror);
    setShowDeleteModal(true);
  };

  const onDocumentErrorEditCancel = () => {
    setDocumentError(undefined);
    setShowEditModal(false);
  };

  const onDocumentErrorEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEditModal(false);
    setDocumentErrors([]);
    setLoading(true);
    fetchDocumentErrors();
  };

  const onDocumentErrorDeleteCancel = () => {
    setDocumentError(undefined);
    setShowDeleteModal(false);
  };

  const onDocumentErrorDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowDeleteModal(false);
    setDocumentErrors([]);
    setLoading(true);
    fetchDocumentErrors();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  useEffect(() => {
    fetchDocumentErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("documentErrors")) {
        return;
      }
      fetchDocumentErrors();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Biblioteca de Errores</h3>
              </div>
              <div className="p-2 ">
                <CButton
                  color="primary"
                  className="float-right"
                  onClick={onAddClick}
                >
                  <BsFillPlusCircleFill />
                  &nbsp; Agregar Nuevo
                </CButton>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={documenterrors}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    rawMessage: (item: DocumentError) => {
                      return (
                        <td className="text-center">
                          {emptyValueOnUndefined(item.rawMessage)}
                        </td>
                      );
                    },
                    possibleSolutions: (item: DocumentError) => {
                      return (
                        <td className="text-center">
                          {emptyValueOnUndefined(item.possibleSolutions)}
                        </td>
                      );
                    },
                    actions: (item: DocumentError) => {
                      const editButton = (
                        <CButton
                          className="text-white"
                          color="warning"
                          onClick={() => {
                            onUpdateClick(item);
                          }}
                        >
                          <i className="fa fa-pencil"></i>
                        </CButton>
                      );
                      const deleteButton = (
                        <CButton
                          className="text-white"
                          color="danger"
                          onClick={() => {
                            onDeleteClick(item);
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </CButton>
                      );

                      return (
                        <td className="text-right">
                          <CButtonGroup>
                            {editButton}
                            {deleteButton}
                          </CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <DocumentErrorUpdateModal
        show={showEditModal}
        onCancel={onDocumentErrorEditCancel}
        onSuccess={onDocumentErrorEditSuccess}
        documentError={documenterror}
      />
      <DocumentErrorDeleteModal
        show={showDeleteModal}
        onCancel={onDocumentErrorDeleteCancel}
        onSuccess={onDocumentErrorDeleteSuccess}
        documentError={documenterror}
      />
    </>
  );
};

export default DocumentErrorList;
